import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  TrendingUp, 
  AlertTriangle, 
  BarChart3, 
  Users, 
  BookOpen,
  FileText,
  MessageSquare 
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EngagementScore {
  userId: string;
  name: string;
  score: number;
  lastActive: string;
}

interface AtRiskMentee {
  userId: string;
  name: string;
  reason: string;
  daysSinceLastActivity: number;
}

interface ResourceUtilization {
  resourceType: string;
  totalItems: number;
  activeUsers: number;
  utilizationRate: number;
}

export function AdminIntelligence() {
  const [engagementScores, setEngagementScores] = useState<EngagementScore[]>([]);
  const [atRiskMentees, setAtRiskMentees] = useState<AtRiskMentee[]>([]);
  const [resourceUtilization, setResourceUtilization] = useState<ResourceUtilization[]>([]);
  const [courseDemand, setCourseDemand] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIntelligenceData();
  }, []);

  const loadIntelligenceData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        calculateEngagementScores(),
        identifyAtRiskMentees(),
        analyzeResourceUtilization(),
        predictCourseDemand()
      ]);
    } catch (error) {
      console.error('Error loading intelligence data:', error);
      toast.error('Failed to load admin intelligence');
    } finally {
      setLoading(false);
    }
  };

  const calculateEngagementScores = async () => {
    // Get activity logs from last 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    const { data: activities } = await supabase
      .from('activity_logs')
      .select('user_id, created_at')
      .gte('created_at', thirtyDaysAgo);

    // Calculate scores based on activity frequency
    const userActivity = activities?.reduce((acc: any, log) => {
      acc[log.user_id] = (acc[log.user_id] || 0) + 1;
      return acc;
    }, {});

    const userIds = Object.keys(userActivity || {});
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', userIds);

    const scores = profiles?.map(profile => {
      const activityCount = userActivity[profile.id] || 0;
      const score = Math.min(Math.round((activityCount / 50) * 100), 100);
      
      const userActivities = activities?.filter(a => a.user_id === profile.id) || [];
      const lastActive = userActivities.length > 0
        ? userActivities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
        : new Date().toISOString();

      return {
        userId: profile.id,
        name: `${profile.first_name} ${profile.last_name}`,
        score,
        lastActive
      };
    }).sort((a, b) => b.score - a.score) || [];

    setEngagementScores(scores.slice(0, 10));
  };

  const identifyAtRiskMentees = async () => {
    // Get mentees who haven't been active recently
    const { data: assignments } = await supabase
      .from('mentor_assignments')
      .select('mentee_id, profiles!mentor_assignments_mentee_id_fkey(id, first_name, last_name)')
      .eq('status', 'active');

    const menteeIds = assignments?.map(a => a.mentee_id) || [];
    
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { data: recentActivity } = await supabase
      .from('activity_logs')
      .select('user_id, created_at')
      .in('user_id', menteeIds)
      .gte('created_at', sevenDaysAgo);

    const activeUsers = new Set(recentActivity?.map(a => a.user_id));
    
    const atRisk = assignments?.filter(a => !activeUsers.has(a.mentee_id))
      .map(a => {
        const profile = a.profiles as any;
        return {
          userId: a.mentee_id,
          name: `${profile?.first_name} ${profile?.last_name}`,
          reason: 'No activity in last 7 days',
          daysSinceLastActivity: 7
        };
      }) || [];

    setAtRiskMentees(atRisk);
  };

  const analyzeResourceUtilization = async () => {
    const resources = [
      { type: 'Videos', table: 'videos' as const, userField: 'uploaded_by' },
      { type: 'Courses', table: 'courses' as const, userField: 'created_by' },
      { type: 'Group Resources', table: 'group_resources' as const, userField: 'user_id' }
    ];

    const utilization = await Promise.all(
      resources.map(async (resource) => {
        const { count: totalItems } = await supabase
          .from(resource.table)
          .select('*', { count: 'exact', head: true });

        const { data: items } = await supabase
          .from(resource.table)
          .select('*');

        const uniqueUsers = new Set(
          items?.map(i => {
            const item = i as any;
            return item[resource.userField];
          }).filter(Boolean)
        ).size;
        
        const { count: totalUsers } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        return {
          resourceType: resource.type,
          totalItems: totalItems || 0,
          activeUsers: uniqueUsers,
          utilizationRate: totalUsers ? Math.round((uniqueUsers / totalUsers) * 100) : 0
        };
      })
    );

    setResourceUtilization(utilization);
  };

  const predictCourseDemand = async () => {
    // Analyze enrollment trends
    const { data: enrollments } = await supabase
      .from('enrollments')
      .select('course_id, courses(title, category_id), enrollment_date')
      .gte('enrollment_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString());

    const courseEnrollments = enrollments?.reduce((acc: any, e) => {
      const courseId = e.course_id;
      const course = e.courses as any;
      if (!acc[courseId]) {
        acc[courseId] = {
          courseId,
          title: course?.title,
          enrollmentCount: 0,
          trend: 'stable'
        };
      }
      acc[courseId].enrollmentCount++;
      return acc;
    }, {});

    const demand = Object.values(courseEnrollments || {})
      .sort((a: any, b: any) => b.enrollmentCount - a.enrollmentCount)
      .slice(0, 5);

    setCourseDemand(demand);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading intelligence data...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <BarChart3 className="h-6 w-6" />
        Admin Intelligence Dashboard
      </h2>

      {/* Engagement Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Top Engagement Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-3">
              {engagementScores.map((user) => (
                <div key={user.userId} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Last active: {new Date(user.lastActive).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge 
                    variant={user.score >= 70 ? 'default' : user.score >= 40 ? 'secondary' : 'outline'}
                  >
                    {user.score}%
                  </Badge>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* At-Risk Mentees */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            At-Risk Mentees
          </CardTitle>
        </CardHeader>
        <CardContent>
          {atRiskMentees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No at-risk mentees identified</p>
          ) : (
            <div className="space-y-3">
              {atRiskMentees.map((mentee) => (
                <div key={mentee.userId} className="p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="font-medium">{mentee.name}</p>
                  <p className="text-sm text-muted-foreground">{mentee.reason}</p>
                  <Badge variant="outline" className="mt-2">
                    {mentee.daysSinceLastActivity} days inactive
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resource Utilization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Resource Utilization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {resourceUtilization.map((resource) => (
              <div key={resource.resourceType} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{resource.resourceType}</span>
                  <span className="text-muted-foreground">
                    {resource.activeUsers} users • {resource.totalItems} items
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${resource.utilizationRate}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{resource.utilizationRate}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Course Demand Predictions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            High-Demand Courses (90 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {courseDemand.map((course: any, index) => (
              <div key={course.courseId} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge variant="outline">#{index + 1}</Badge>
                  <span className="font-medium">{course.title}</span>
                </div>
                <Badge>{course.enrollmentCount} enrollments</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={loadIntelligenceData} variant="outline" className="w-full">
        Refresh Intelligence Data
      </Button>
    </div>
  );
}
